import cn from "classnames";
import { CheckIcon } from "@heroicons/react/24/outline";
import { SURVEY_OTHER_MAX_CHARS, type SurveyOption } from "../../../common/postTrialSurvey";

type Shared = {
  options: SurveyOption[];
  // Free text for an option flagged allowText ("Other").
  otherText?: string;
  onOtherTextChange?: (text: string) => void;
};

type SingleProps = Shared & {
  mode: "single";
  value: string | undefined;
  onChange: (value: string) => void;
};

type MultiProps = Shared & {
  mode: "multi";
  value: string[];
  maxPicks?: number;
  onChange: (value: string[]) => void;
};

type Props = SingleProps | MultiProps;

// Full-width tappable rows. Single-choice rows behave like radios; multi
// rows show the app's checkbox idiom and respect maxPicks by disabling the
// remaining options once the cap is hit.
const ChoiceQuestion: React.FC<Props> = (props) => {
  const { options, otherText, onOtherTextChange } = props;
  const isMulti = props.mode === "multi";
  const selected = new Set(isMulti ? props.value : props.value ? [props.value] : []);
  const capped = isMulti && !!props.maxPicks && props.value.length >= props.maxPicks;

  const toggle = (value: string) => {
    if (!isMulti) {
      props.onChange(value);
      return;
    }
    if (selected.has(value)) {
      props.onChange(props.value.filter((v) => v !== value));
    } else if (!capped) {
      props.onChange([...props.value, value]);
    }
  };

  return (
    <div className="flex flex-col gap-2" role={isMulti ? "group" : "radiogroup"}>
      {options.map((o, i) => {
        const isSelected = selected.has(o.value);
        const disabled = isMulti && capped && !isSelected;
        return (
          <div key={o.value} className="flex flex-col gap-2">
          <button
            type="button"
            role={isMulti ? "checkbox" : "radio"}
            aria-checked={isSelected}
            aria-disabled={disabled}
            onClick={() => !disabled && toggle(o.value)}
            className={cn(
              "flex items-center gap-3 w-full text-left rounded-xl border px-4 py-3 text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
              isSelected
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300"
                : "border-base-300 bg-base-100 text-base-content/80 hover:bg-base-200",
              disabled && "opacity-40 cursor-not-allowed hover:bg-base-100"
            )}
          >
            {isMulti ? (
              <span
                className={cn(
                  "size-5 shrink-0 rounded-md border flex items-center justify-center",
                  isSelected ? "bg-blue-500 border-blue-500 text-white" : "border-base-content/30"
                )}
                aria-hidden
              >
                {isSelected && <CheckIcon className="size-3.5" strokeWidth={3} />}
              </span>
            ) : (
              <span
                className={cn(
                  "size-5 shrink-0 rounded-full border flex items-center justify-center",
                  isSelected ? "border-blue-500" : "border-base-content/30"
                )}
                aria-hidden
              >
                {isSelected && <span className="size-2.5 rounded-full bg-blue-500" />}
              </span>
            )}
            <span className="leading-snug">{o.label}</span>
          </button>
          {o.allowText && isSelected && (
            <div>
              <textarea
                autoFocus
                rows={3}
                value={otherText ?? ""}
                maxLength={SURVEY_OTHER_MAX_CHARS}
                onChange={(e) => onOtherTextChange?.(e.target.value)}
                placeholder="Tell us more"
                className="w-full rounded-lg border border-base-300 bg-base-100 px-3 py-2 text-sm text-base-content placeholder:text-base-content/40 outline-none focus:border-blue-500 transition-colors resize-none"
              />
              <div className="mt-1 text-right text-[11px] text-base-content/40 tabular-nums">
                {(otherText ?? "").length}/{SURVEY_OTHER_MAX_CHARS}
              </div>
            </div>
          )}
          </div>
        );
      })}
    </div>
  );
};

export default ChoiceQuestion;
